import { useQueries } from "@tanstack/react-query";
import { useRef } from "react";
import type { ResolvedPlace } from "../../shared/types";
import { fetchResolvedStore } from "../lib/api";

export interface ResolvedStoresResult {
  byId: ReadonlyMap<string, ResolvedPlace>;
  isLoading: boolean;
  isError: boolean;
}

/**
 * ids 配列に含まれる店舗の place resolve を個別にクエリし、
 * 解決済みの ResolvedPlace を `byId` Map として返す。
 *
 * パフォーマンス設計:
 *   以前は毎 render で `ids.join("|")` と `queries.map(...).join("|")` の
 *   2 つの巨大な文字列を生成して useMemo key にしていた (200 件規模で GC 圧迫)。
 *   さらに `byId` の identity が毎 tick ごとに変わるため、下流の MapView の
 *   useMemo / React.memo が軒並み無効化される問題があった。
 *
 *   修正: byId を useRef で持ち、「前回と比べて実際に新しい ResolvedPlace が
 *   追加された / 既存値が入れ替わった場合のみ」新 Map を生成する。
 *   変化なしなら同じ Map 参照を返し続け、下流の referential equality が維持される。
 *   進行中の batch tick でも、resolve が完了した tick 以外は byId 参照が変わらない。
 */
export function useResolvedStores(ids: string[]): ResolvedStoresResult {
  const queries = useQueries({
    queries: ids.map((id) => ({
      queryKey: ["resolve", id] as const,
      // signal を流すことで React Query が非アクティブ query の inflight fetch を
      // abort できる。フィルタ/検索変更で ids から外れた未完了クエリが走り続けて
      // Places API に課金されるのを防ぐ (cost-saving の実効化)。
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        fetchResolvedStore(id, signal),
      staleTime: 1000 * 60 * 60 * 24 * 7, // 7 days on client
      gcTime: 1000 * 60 * 60 * 24 * 7,
      retry: 0,
    })),
  });

  const byIdRef = useRef<Map<string, ResolvedPlace>>(new Map());

  // 現在の ids + queries の結果を走査し、既存の byIdRef と差分があるときだけ
  // 新 Map を生成する。差分検出は:
  //   1. ids 集合から外れたエントリがあれば削除
  //   2. 新しく resolve された id があれば追加
  //   3. 既存 id の ResolvedPlace 参照が変わっていれば更新 (通常は起きない)
  const prev = byIdRef.current;
  let mutated = false;
  // 集合差分を取るための新 id セット。
  const currentIds = new Set<string>();
  for (let i = 0; i < ids.length; i++) {
    const id = ids[i];
    if (!id) continue;
    currentIds.add(id);
    const data = queries[i]?.data;
    if (!data) continue;
    const existing = prev.get(id);
    if (existing !== data) {
      if (!mutated) {
        // 最初の変更で shallow copy を作り、以降はこのコピーを書き換える。
        byIdRef.current = new Map(prev);
        mutated = true;
      }
      byIdRef.current.set(id, data);
    }
  }
  // ids から外れた古いエントリを prune。
  for (const id of prev.keys()) {
    if (!currentIds.has(id)) {
      if (!mutated) {
        byIdRef.current = new Map(prev);
        mutated = true;
      }
      byIdRef.current.delete(id);
    }
  }

  // `some()` も per render で走るが 200 件なら無視できる小さな定数。
  // 将来ボトルネックになったら loading/error のカウントを上の差分ループで一緒に取る。
  let isLoading = false;
  let isError = false;
  for (const q of queries) {
    if (q.isLoading) isLoading = true;
    if (q.isError) isError = true;
  }

  return {
    byId: byIdRef.current,
    isLoading,
    isError,
  };
}
